/**
 * Table View Component
 * ====================
 * Displays Excel data in a scrollable table with sorting and filtering
 */

import React, { useState, useMemo } from 'react';

import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download, Copy, Check } from 'lucide-react';

export default function TableView({ data, schema, maxRows = 100, onQueryRequest }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);

  // Get headers from data or schema
  const headers = useMemo(() => {
    if (data && data.length > 0) {
      return Object.keys(data[0]);
    }
    if (schema?.sheets?.[0]?.columns) {
      return schema.sheets[0].columns.map(c => c.name);
    }
    return [];
  }, [data, schema]);

  // Get column types from schema
  const columnTypes = useMemo(() => {
    const types = {};
    schema?.sheets?.[0]?.columns?.forEach(col => {
      types[col.name] = col.type;
    });
    return types;
  }, [schema]);

  // Sort and filter data
  const displayData = useMemo(() => {
    let result = [...(data || [])];

    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(row =>
        Object.values(row).some(val => String(val).toLowerCase().includes(lowerSearch))
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        // Handle numeric sorting
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        // String sorting
        const aStr = String(aVal || '');
        const bStr = String(bVal || '');
        return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
      });
    }

    // Limit rows
    return result.slice(0, maxRows);
  }, [data, searchTerm, sortConfig, maxRows]);

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleCopy = async () => {
    try {
      const csvContent = [
        headers.join('\t'),
        ...displayData.map(row => headers.map(h => row[h] || '').join('\t')),
      ].join('\n');

      await navigator.clipboard.writeText(csvContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="table-view-empty">
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div className="table-view">
      {/* Toolbar */}
      <div className="table-toolbar">
        <div className="search-box">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search data..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="toolbar-actions">
          <button className="toolbar-btn" onClick={handleCopy} title="Copy to clipboard">
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-500" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span>Copy</span>
              </>
            )}
          </button>

          <span className="row-count">
            Showing {displayData.length} of {data.length} rows
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="row-number">#</th>
              {headers.map(header => (
                <th
                  key={header}
                  className={`sortable ${sortConfig.key === header ? 'sorted' : ''}`}
                  onClick={() => handleSort(header)}
                >
                  <span className="header-content">
                    <span className="header-text">{header}</span>
                    {columnTypes[header] && (
                      <span className={`type-indicator type-${columnTypes[header]}`}>
                        {getTypeIndicator(columnTypes[header])}
                      </span>
                    )}
                  </span>
                  <span className="sort-icon">
                    {sortConfig.key === header ? (
                      sortConfig.direction === 'asc' ? (
                        <ArrowUp className="w-3 h-3" />
                      ) : (
                        <ArrowDown className="w-3 h-3" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, rowIdx) => (
              <tr key={rowIdx}>
                <td className="row-number">{rowIdx + 1}</td>
                {headers.map(header => (
                  <td key={header} className={`cell ${columnTypes[header] || 'string'}`}>
                    {formatCellValue(row[header], columnTypes[header])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {data.length > maxRows && (
        <div className="table-footer">
          <span className="truncation-note">
            Showing first {maxRows} rows. Use queries for full data analysis.
          </span>
          {onQueryRequest && (
            <button className="query-btn" onClick={() => onQueryRequest('SELECT * FROM $FILE')}>
              Run SQL Query
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function getTypeIndicator(type) {
  switch (type) {
    case 'integer':
      return '#';
    case 'float':
      return '0.0';
    case 'string':
      return 'Aa';
    case 'datetime':
      return '📅';
    case 'boolean':
      return '✓';
    default:
      return '';
  }
}

function formatCellValue(value, type) {
  if (value === null || value === undefined) {
    return <span className="null-value">—</span>;
  }

  if (type === 'float' && typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  if (type === 'integer' && typeof value === 'number') {
    return value.toLocaleString();
  }

  const strValue = String(value);
  if (strValue.length > 50) {
    return <span title={strValue}>{strValue.slice(0, 47)}...</span>;
  }

  return strValue;
}
